/**
 * Ghost layer for the all-floors plan views.
 *
 * Upper stories can't be de-emphasised with `globalAlpha` alone: in the
 * default (Bold) floor plan the structure walls are already light grey, so a
 * 35%-alpha copy is near-white and reads as "missing", not "above". Instead,
 * `paint` is rendered to an offscreen layer, the layer is tinted (so hue —
 * not just lightness — separates ghost from solid), and the whole layer is
 * composited once at `alpha`. Compositing the layer as a unit also stops
 * overlapping strokes/fills from stacking darker than the intended alpha.
 *
 * `paint(ctx2)` receives a context carrying the caller's current transform
 * and a canvas of the same size, so drawing code that reads
 * `ctx.canvas.width/height` (e.g. `drawWalls`) behaves identically.
 */

// Cool slate-blue wash. Alpha here is the tint STRENGTH on the layer (how far
// greys shift toward blue); the layer's overall opacity is the caller's alpha.
export const GHOST_TINT = 'rgba(99, 125, 168, 0.55)';

const layerCache = new WeakMap();

const getLayer = (canvas) => {
  let off = layerCache.get(canvas);
  if (!off) {
    off = document.createElement('canvas');
    layerCache.set(canvas, off);
  }
  if (off.width !== canvas.width || off.height !== canvas.height) {
    off.width = canvas.width;
    off.height = canvas.height;
  }
  return off;
};

export function withGhostLayer(ctx, { alpha, tint = GHOST_TINT }, paint) {
  if (alpha >= 1) {
    paint(ctx);
    return;
  }
  const canvas = ctx.canvas;
  const canLayer =
    typeof ctx.getTransform === 'function' &&
    typeof document !== 'undefined' &&
    canvas?.width > 0 &&
    canvas?.height > 0;
  const octx = canLayer ? getLayer(canvas).getContext('2d') : null;
  if (!octx) {
    // No offscreen support (tests / exotic contexts): plain alpha fallback.
    ctx.save();
    ctx.globalAlpha = alpha;
    paint(ctx);
    ctx.restore();
    return;
  }
  const { width, height } = canvas;

  octx.save();
  octx.setTransform(1, 0, 0, 1, 0, 0);
  octx.clearRect(0, 0, width, height);
  octx.restore();

  octx.save();
  octx.setTransform(ctx.getTransform());
  paint(octx);
  octx.restore();

  // Tint only where something was painted.
  octx.save();
  octx.setTransform(1, 0, 0, 1, 0, 0);
  octx.globalCompositeOperation = 'source-atop';
  octx.fillStyle = tint;
  octx.fillRect(0, 0, width, height);
  octx.restore();

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = alpha;
  ctx.drawImage(octx.canvas, 0, 0);
  ctx.restore();
}
